import Phaser from 'phaser';
import { t, setLocale, getLocale, LOCALES, type Locale } from '@i18n/index';
import { PALETTE, TEXT_COLOUR, titleStyle, readoutStyle, FONT, px, getReducedMotion } from '../theme';
import { createButton } from '../ui/button';
import { generateSeed } from '@core/rng';
import { hasSave, loadCampaign } from '../saveGame';
import { audio } from '../audio/engine';
import { BASE_MOTIF, DRONE_ROOT } from '../audio/motifs';
import type { MapData } from '../mapData';
import { GAME_VERSION } from '../version';
import { SonarScope } from '../render/sonarScope';
import { createSettingsButton, persistSettings } from '../ui/settingsPanel';
import { layoutOf, makeTappable, watchResize } from '../ui/layout';
import { onTap } from '../ui/tap';
import { addBackdrop, MENU_BACKDROP } from '../assets';

/**
 * Das Startmenue.
 *
 * Der Seed steht bewusst sichtbar davor: Er bestimmt den gesamten Durchlauf
 * einschliesslich der Aufloesung, und ihn weitergeben zu koennen macht aus
 * einem Einzelspiel eine Sache, ueber die man reden kann.
 */
export class MenuScene extends Phaser.Scene {
  private seed = generateSeed();
  private scope: SonarScope | null = null;
  private backdrop: Phaser.GameObjects.Image | null = null;

  constructor() {
    super('Menu');
  }

  create(): void {
    this.cameras.main.setBackgroundColor(PALETTE.abyss);

    // Der Schirm liegt hinter allem und wird beim Neuzeichnen nicht
    // mitgeloescht — sonst setzte der Strahl bei jedem Klick zurueck.
    const { width, height } = this.scale;
    this.backdrop = this.createBackdrop();
    this.scope = new SonarScope(
      this,
      width / 2,
      height * 0.46,
      Math.min(width, height) * 0.42,
    );

    this.redraw();
    this.cameras.main.fadeIn(getReducedMotion() ? 0 : 500, 5, 8, 13);

    // Der Sonarschirm haengt an der Fenstergroesse; nach einer Drehung ist ein
    // Neustart der Szene einfacher, als alles einzeln umzusetzen.
    watchResize(this, () => this.scene.restart());

    // Tastatur: N fuer neu, F (Fortsetzen) oder C (continue) zum Weiterspielen.
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if (key === 'n') this.startNew();
      if ((key === 'f' || key === 'c') && hasSave()) this.continueGame();
    });

    // Browser lassen Audio erst nach einer Nutzerhandlung zu. Der erste Klick
    // irgendwo im Menü genügt.
    this.input.once('pointerdown', () => {
      audio.resume();
      audio.startDrone(DRONE_ROOT.menu);
      audio.playMotif(BASE_MOTIF, 0.07);
    });
  }

  /**
   * Das gestrandete Wrack unter Sternen als Hintergrund.
   *
   * Es liegt hinter dem Sonarschirm und ist stark abgedunkelt — das Menue
   * bleibt eine Textseite, aber man weiss nach einer Sekunde, worum es in
   * diesem Spiel geht. Fehlt die Datei, bleibt es beim schwarzen Grund.
   */
  private createBackdrop(): Phaser.GameObjects.Image | null {
    return addBackdrop(this, MENU_BACKDROP, 0.42);
  }

  override update(_time: number, deltaMs: number): void {
    this.scope?.update(deltaMs, this.time.now);
  }

  private redraw(): void {
    // Nur die Bedienelemente raeumen, den Sonarschirm stehen lassen — sonst
    // setzte der Strahl bei jedem Klick zurueck.
    const keep = new Set<Phaser.GameObjects.GameObject>(this.scope?.objects ?? []);
    if (this.backdrop !== null) keep.add(this.backdrop);
    for (const child of [...this.children.list]) {
      if (!keep.has(child)) child.destroy();
    }

    const layout = layoutOf(this);
    const { width, height } = layout;
    const centreX = width / 2;

    /*
     * Von oben nach unten gestapelt, jede Zeile nach der Hoehe der vorigen.
     *
     * Frueher standen die Bloecke bei festen Anteilen der Fensterhoehe (24 %,
     * 46 %, 68 %). Auf einem Telefon im Querformat lagen sie dadurch
     * uebereinander, im Hochformat brach der Titel ueber den Rand.
     */
    const titleSize = layout.compact ? 26 : 38;
    const titleText = this.add
      .text(centreX, 0, t('game.title'), {
        ...titleStyle(titleSize),
        align: 'center',
        wordWrap: { width: layout.columnWidth },
      })
      .setOrigin(0.5, 0);
    const subtitle = this.add
      .text(centreX, 0, t('menu.subtitle'), {
        ...readoutStyle(12, true),
        fontStyle: 'italic',
        align: 'center',
        wordWrap: { width: layout.columnWidth },
      })
      .setOrigin(0.5, 0);

    const blockHeight = titleText.height + 10 + subtitle.height;
    const titleTop = Math.max(layout.short ? 44 : 60, height * 0.2 - blockHeight / 2);
    titleText.y = titleTop;
    subtitle.y = titleTop + titleText.height + 10;
    const ruleY = subtitle.y + subtitle.height + 16;
    this.add.rectangle(centreX, ruleY, Math.min(300, layout.columnWidth), 1, PALETTE.panelEdge);

    // Dieselben Einstellungen wie im Spiel — hier zusaetzlich zu den Zeilen
    // unten, weil die Schriftgroesse dort nicht steht und man sie schon vor
    // dem ersten Text brauchen kann.
    createSettingsButton(this, this.scale.width - 24, 26, () => this.redraw());

    // Die Hauptknoepfe sitzen etwa in der Mitte, aber nie hoeher als unter dem Titel.
    let y = Math.max(ruleY + 44, height * 0.46);
    const buttonWidth = Math.min(260, layout.columnWidth);
    const newGame = createButton(this, centreX, y, t('menu.new'), () => this.startNew(), {
      width: buttonWidth,
    });
    y += newGame.height + 12;

    if (hasSave()) {
      const cont = createButton(this, centreX, y, t('menu.continue'), () => this.continueGame(), {
        width: buttonWidth,
      });
      y += cont.height + 12;
    }

    // Die Zeilen darunter rutschen nach unten, wenn Platz ist, aber nie ins Impressum.
    y = Math.max(y + 8, Math.min(height * 0.66, height - 190));
    y = this.renderSeed(centreX, y, layout.compact);
    y = this.renderLanguage(centreX, y + 4);
    this.renderSound(centreX, y);

    // Impressum muss ohne Umwege erreichbar sein.
    const imprint = this.add
      .text(centreX, height - 30, t('menu.imprint'), {
        fontFamily: FONT.mono,
        fontSize: px(11),
        color: PALETTE.inkFaint,
      })
      .setOrigin(0.5);
    makeTappable(imprint);
    onTap(imprint, {
      onTap: () => this.scene.start('Imprint'),
      onHighlight: (on) => imprint.setColor(on ? TEXT_COLOUR.coast : PALETTE.inkFaint),
    });

    this.add
      .text(width - 14, height - 14, `v${GAME_VERSION}`, {
        fontFamily: FONT.mono,
        fontSize: px(10),
        color: PALETTE.inkFaint,
      })
      .setOrigin(1, 1);
  }

  /**
   * Ein neues Spiel beginnt mit dem Prolog, nicht mit dem Editor — die erste
   * Entscheidung soll fallen, bevor man irgendetwas einstellt.
   */
  private startNew(): void {
    this.registry.set('pendingSeed', this.seed);
    this.registry.remove('prologueChoice');
    this.scene.start('Prologue');
  }

  /** Liefert die Unterkante der Zeile. */
  private renderSeed(centreX: number, top: number, compact: boolean): number {
    const y = top + 22;
    const label = this.add
      .text(centreX - 110, y, t('menu.seed'), {
        fontFamily: FONT.mono,
        fontSize: px(11),
        color: PALETTE.inkFaint,
      })
      .setOrigin(1, 0.5);

    const seed = this.add
      .text(centreX - 90, y, this.seed, {
        fontFamily: FONT.mono,
        fontSize: px(15),
        color: TEXT_COLOUR.coast,
      })
      .setOrigin(0, 0.5);

    const reroll = (): void => {
      this.seed = generateSeed();
      this.redraw();
    };

    if (!compact) {
      createButton(this, centreX + 140, y, t('menu.seed.random'), reroll, {
        width: 140,
        height: 44,
        fontSize: 11,
      });
      return y + 22;
    }

    // Schmal: Beschriftung und Wert mittig, der Knopf darunter.
    label.setX(centreX - 8);
    seed.setX(centreX + 8);
    const button = createButton(this, centreX, y + 44, t('menu.seed.random'), reroll, {
      width: 160,
      height: 44,
      fontSize: 11,
    });
    return button.y + button.height / 2;
  }

  /** Liefert die Unterkante der Zeile. */
  private renderLanguage(centreX: number, top: number): number {
    const y = top + 22;
    this.add
      .text(centreX - 110, y, t('menu.language'), {
        fontFamily: FONT.mono,
        fontSize: px(11),
        color: PALETTE.inkFaint,
      })
      .setOrigin(1, 0.5);

    LOCALES.forEach((locale, index) => {
      const active = getLocale() === locale;
      const label = this.add
        .text(centreX - 84 + index * 46, y, locale.toUpperCase(), {
          fontFamily: FONT.mono,
          fontSize: px(13),
          color: active ? TEXT_COLOUR.coast : PALETTE.inkFaint,
        })
        .setOrigin(0, 0.5);
      makeTappable(label);
      onTap(label, { onTap: () => this.switchLanguage(locale) });
    });
    return y + 22;
  }

  private renderSound(centreX: number, top: number): void {
    const y = top + 22;
    this.add
      .text(centreX - 110, y, t('menu.sound'), {
        fontFamily: FONT.mono,
        fontSize: px(11),
        color: PALETTE.inkFaint,
      })
      .setOrigin(1, 0.5);

    const label = this.add
      .text(centreX - 84, y, t(audio.isMuted ? 'menu.sound.off' : 'menu.sound.on'), {
        fontFamily: FONT.mono,
        fontSize: px(13),
        color: audio.isMuted ? PALETTE.inkFaint : TEXT_COLOUR.coast,
      })
      .setOrigin(0, 0.5);
    makeTappable(label);
    onTap(label, {
      onTap: () => {
        audio.setMuted(!audio.isMuted);
        // Alle Einstellungen sichern, nicht nur diese zwei — frueher ging hier
        // die gewaehlte Schriftgroesse verloren.
        persistSettings();
        this.redraw();
      },
    });
  }

  private switchLanguage(locale: Locale): void {
    setLocale(locale);
    persistSettings();
    this.redraw();
  }

  private continueGame(): void {
    const mapData = this.registry.get('mapData') as MapData;
    const campaign = loadCampaign(mapData.bathymetry, mapData.coastline);

    if (campaign === null) {
      // Der Stand war unlesbar — dann bleibt nur ein neuer Anfang.
      this.redraw();
      return;
    }

    this.registry.set('campaign', campaign);
    this.scene.start('WorldMap');
  }
}
